/* Shared loading / error state views for the Courier Setup screens, so every
   list + dashboard page handles its states identically and looks production-ready:
   - <TableSkeleton>   — skeleton placeholder mirroring a KPI strip + data table
   - <LoadErrorPanel>  — themed error card with a Retry action
   (Empty states are handled by SupplyTable / per-screen empty panels.) */
import React from 'react'
import PropTypes from 'prop-types'
import {alpha} from '@mui/material/styles'
import {Box, Skeleton, Button, Typography} from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

export function TableSkeleton({rows = 8, kpis = 0}) {
  return (
    <Box>
      {kpis > 0 ? (
        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5, mb: 2}}>
          {Array.from({length: kpis}).map((_, i) => (
            <Skeleton key={i} variant='rounded' height={92} sx={{borderRadius: '14px'}} />
          ))}
        </Box>
      ) : null}
      <Box sx={{border: 1, borderColor: 'divider', borderRadius: '14px', overflow: 'hidden', bgcolor: 'background.paper'}}>
        <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1.5, alignItems: 'center'}}>
          <Skeleton variant='rounded' width={260} height={36} />
          <Box sx={{flex: 1}} />
          <Skeleton variant='rounded' width={120} height={36} />
        </Box>
        {Array.from({length: rows}).map((_, i) => (
          <Box
            key={i}
            sx={{px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2}}
          >
            <Skeleton variant='circular' width={28} height={28} />
            <Skeleton variant='text' sx={{flex: 1, fontSize: 14}} />
            <Skeleton variant='text' width={90} />
            <Skeleton variant='rounded' width={70} height={22} />
            <Skeleton variant='text' width={60} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}
TableSkeleton.propTypes = {rows: PropTypes.number, kpis: PropTypes.number}

export function LoadErrorPanel({message, onRetry, title = 'Couldn’t load this'}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        p: {xs: 4, md: 6},
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          mx: 'auto',
          mb: 2,
          bgcolor: (t) => alpha(t.palette.error.main, 0.1),
          color: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <WarningAmberOutlinedIcon sx={{fontSize: 28}} />
      </Box>
      <Typography sx={{fontWeight: 700, fontSize: 16, color: 'text.primary'}}>{title}</Typography>
      <Typography sx={{color: 'text.secondary', fontSize: 13.5, mt: 0.75, maxWidth: 440, mx: 'auto'}}>{message}</Typography>
      {onRetry ? (
        <Button variant='outlined' onClick={onRetry} sx={{mt: 2.5, borderRadius: '12px', px: 3}}>
          Retry
        </Button>
      ) : null}
    </Box>
  )
}
LoadErrorPanel.propTypes = {message: PropTypes.string, onRetry: PropTypes.func, title: PropTypes.string}
